import { FC } from 'react';
import { css } from '@emotion/react';
import { Address } from '@weave/schema-gen-ts/dist/schemas/phone/address/v1/address_service.pb';
import { useQuery, useQueryClient } from 'react-query';
import { E911Api, E911QueryKeys } from '@frontend/api-e911-addresses';
import { Trans, useTranslation } from '@frontend/i18n';
import { FormRow, Text, useAlert } from '@frontend/design-system';
import { CreateSoftphonePropTypes, inputFieldStyles } from '..';
// import { useCreateSoftphone } from '../../../../store/create-softphone-provider';
import { formatE911AddressForDisplay } from '../../../../utils/e911-address';
import { E911AddressForm } from '../../e911/e911-address-form';
import { E911Select } from '../../e911/e911-select';
import { getE911CreateSuccessMessage, useCreateE911Address } from '../../e911/use-e911-address-form';

type Props = CreateSoftphonePropTypes;

export const ThirdStep: FC<React.PropsWithChildren<Props>> = ({ getFieldProps, values, tenantId }: Props) => {
  const { data: { addresses } = { addresses: [] } } = useQuery({
    queryKey: [tenantId, ...E911QueryKeys.queryKeys.listAddresses()],
    queryFn: () => E911Api.list({ tenantId: tenantId ?? '' }),
    meta: {
      error: {
        message: 'ThirdStep:addressesQuery | Failed to fetch E911 addresses',
        data: { tenantId },
      },
    },
  });

  const queryClient = useQueryClient();
  const alerts = useAlert();

  const { t } = useTranslation('phone', { keyPrefix: 'new-softphone' });
  const addNewAddress = getFieldProps('assignedAddressID').value === 'addNewAddress';

  const goBack = (id?: string) => {
    getFieldProps('assignedAddressID').onChange({ name: 'assignedAddressID', value: id });
  };

  const {
    createAddress,
    isCreatingAddress: isLoadingCreateAddress,
    addressSaveError,
    clearAddressSaveError,
  } = useCreateE911Address({
    tenantId,
    onSuccess: (data, submittedAddress) => {
      if (addresses?.some(({ id }) => id === data.id)) {
        alerts.warning(
          t('Duplicate address. {{addressName}} ({{address}})', {
            addressName: data.name || t('Address'),
            address: formatE911AddressForDisplay(data),
          })
        );
      } else {
        alerts.success(getE911CreateSuccessMessage(t, submittedAddress, data));
      }
      queryClient.invalidateQueries([tenantId, ...E911QueryKeys.queryKeys.listAddresses()]);
      goBack(data.id);
    },
  });

  const handleNewAddressSave = (values: Address) => {
    createAddress({ ...values });
  };

  return (
    <>
      {addNewAddress ? (
        <>
          <Text>
            <Trans t={t} deviceName={values?.deviceName} firstName={values?.firstName}>
              An E911 address assigned to <b>{values?.deviceName}</b> is a federal regulation and provides{' '}
              <b>{values?.firstName}’s</b> physical location for emergency services. Add an address that is the primary
              working location where <b>{values?.firstName}</b> will be using this softphone.
            </Trans>
          </Text>
          <E911AddressForm
            onBack={goBack}
            onSave={handleNewAddressSave}
            isForm={false}
            isLoading={isLoadingCreateAddress}
            addressSaveError={addressSaveError}
            clearAddressSaveError={clearAddressSaveError}
            footerStyles={css`
              padding: 0;
            `}
          />
        </>
      ) : (
        <>
          <Text>
            <Trans t={t} deviceName={values?.deviceName} firstName={values?.firstName}>
              An E911 address assigned to <b>{values?.deviceName}</b> is a federal regulation and provides{' '}
              <b>{values?.firstName}’s</b> physical location for emergency services. Assign a primary working location
              where <b>{values?.firstName}</b> will be using this softphone.
            </Trans>
          </Text>
          <FormRow css={inputFieldStyles}>
            <E911Select
              label={t('Select an E911 Address')}
              addAnAddress
              addressId={values?.assignedAddressID ?? ''}
              addresses={addresses}
              onChange={(id) => {
                getFieldProps('assignedAddressID').onChange({ value: id, name: 'assignedAddressID' });
              }}
            />
          </FormRow>
        </>
      )}
    </>
  );
};
